import { locatedAnchor } from '../../../../shared/routeAnchor';
import { adjustSignalCount, signalCounts, signalSummary } from '../../logic/signalCounts';
import type { SignalKind } from '../../../../shared/enums';
import type { Signal, SignalList } from '../../../../shared/types';
import { shell } from '../../app';
import { NetworkError } from '../../data/api';
import { loadRoute } from '../../data/corridor';
import { currentPosition } from '../../data/geocode';
import { forgetStoredSignal, loadNewSignals } from '../../data/signals';
import { formatDateTime, todayLocal } from '../../logic/dates';
import { emptyFilters, matchesFilters } from '../../logic/signalFilters';
import type { Filters } from '../../logic/signalFilters';
import { sectionsOf } from '../../logic/signalRank';
import type { Sections } from '../../logic/signalRank';
import type { LatLng } from '../../logic/route';
import { locationOf, planSignalRoute } from '../../logic/signals';
import { sourceRows, trustLine } from '../../logic/sourceHealth';
import type { SourceRows } from '../../logic/sourceHealth';
import { commitPlannedRoute, planErrorMessage, routeAnchor } from '../routeCommit';
import { createTriage } from './inboxTriage';
import type { Triage } from './inboxTriage';

/** The New businesses inbox (D57, D72, D74): the sorted list and the hidden fold, the filters, the picks
 *  and the triage (inboxTriage.ts), shared by the phone and the desktop. Views read it and repaint on
 *  every change. */

export interface InboxState {
  loaded: boolean;
  error: { text: string; hint?: string } | null;
  /** "Offline · showing the list from Oct 12, 9:41 AM" while the list is the stored copy. */
  offlineNote: string | null;
  /** When the list on screen was fetched. */
  updatedAt: string | null;
  /** The trust line and the sources sheet's rows (D74), and the month's Google lookups (D79). */
  sources: {
    trust: { text: string; needsYou: number; failing: number };
    rows: SourceRows;
    google?: { lookups_month: number; cap: number };
  } | null;
  planning: boolean;
  /** Where the rep is, for Near you and the distances (D80): the day route's anchor, else the phone's
   *  position; null until known, or when neither is. */
  anchor: LatLng | null;
}

export interface Inbox extends Triage {
  /** The queue: the top and the rest, in the server's order. */
  readonly all: readonly Signal[];
  readonly hidden: readonly Signal[];
  readonly list: SignalList | null;
  readonly summary: ReturnType<typeof signalSummary>;
  readonly kinds: Set<SignalKind>;
  /** Every filter; `kinds` is its category one. */
  readonly filters: Filters;
  readonly picked: Set<string>;
  readonly state: InboxState;
  visible: () => Signal[];
  sections: () => Sections;
  /** A view that shows Near you and distances asks once where the rep is (D80); the sources page never does. */
  locate: () => void;
  load: () => Promise<void>;
  routePicks: () => Promise<void>;
  dispose: () => void;
}

export const createInbox = (onChange: () => void, opts: { keepEmptyKinds?: boolean } = {}): Inbox => {
  let all: Signal[] = [];
  let hidden: Signal[] = [];
  let list: SignalList | null = null;
  let counts: SignalList['counts'] = { top: 0, queue: 0, hidden: 0 };
  let disposed = false;
  const kinds = new Set<SignalKind>(); // none picked = all kinds
  const filters: Filters = { ...emptyFilters(), kind: kinds };
  const picked = new Set<string>();
  const state: InboxState = {
    loaded: false,
    error: null,
    offlineNote: null,
    updatedAt: null,
    sources: null,
    planning: false,
    anchor: null,
  };
  const changed = () => {
    if (!disposed) onChange();
  };
  // the anchor is asked for once a view wants it, and comes when it comes (the phone's position may
  // take a moment): the list is painted again with it, never held for it
  const anchorOf = async (): Promise<LatLng | null> => {
    const route = loadRoute();
    return locatedAnchor(route) ?? currentPosition(3000);
  };
  let locating = false;
  const locate = () => {
    if (locating) return;
    locating = true;
    void anchorOf().then((at) => {
      if (!at) return;
      state.anchor = at;
      changed();
    });
  };

  // a kind whose last signal was triaged must not stay selected with no chip to clear it (the phone);
  // the desktop keeps every tick, shown at 0 with a tag, so a refused triage that brings a business
  // back finds its filter still on (D66) — Category included (keepEmptyKinds)
  const prune = () => {
    const present = new Set(all.map((s) => s.kind));
    if (!opts.keepEmptyKinds) for (const k of kinds) if (present.size < 2 || !present.has(k)) kinds.delete(k);
    // a pick is on screen in the queue or in the hidden fold (the checkbox is on both cards)
    for (const id of picked)
      if (!all.some((s) => s.id === id) && !hidden.some((s) => s.id === id)) picked.delete(id);
  };

  const load = async () => {
    try {
      const res = await loadNewSignals();
      if (disposed) return;
      all = res.list.signals;
      hidden = res.list.hidden;
      list = res.list;
      counts = signalCounts(res.list);
      const now = new Date();
      state.sources = {
        trust: trustLine(res.list, now),
        rows: sourceRows(res.list, now),
        ...(res.list.google ? { google: res.list.google } : {}),
      };
      state.updatedAt = res.savedAt;
      state.offlineNote = res.offline
        ? `Offline · showing the list from ${formatDateTime(res.savedAt, todayLocal())}`
        : null;
      state.error = null;
    } catch (err) {
      if (disposed) return;
      state.error =
        err instanceof NetworkError
          ? {
              text: 'New businesses need a connection.',
              hint: 'They are checked on the server during the day; open this again when you have signal.',
            }
          : { text: 'Could not load new businesses.', hint: err instanceof Error ? err.message : undefined };
      // an older list stays on screen (the views show the error only with nothing to show), so a
      // refresh that failed after an import must not pass for the current list
      if (all.length) shell.toast(state.error.text, { kind: 'error' });
    }
    state.loaded = true;
    prune();
    changed();
  };

  const drop = (id: string) => {
    adjustSignalCount(counts, [...all, ...hidden].find((s) => s.id === id), -1);
    all = all.filter((s) => s.id !== id);
    hidden = hidden.filter((s) => s.id !== id);
    prune();
    changed();
  };
  const move = (s: Signal, to: 'all' | 'hidden') => {
    adjustSignalCount(counts, [...all, ...hidden].find((x) => x.id === s.id), -1);
    adjustSignalCount(counts, { ...s, bucket: to === 'all' ? 'queue' : 'hidden' }, 1);
    all = all.filter((x) => x.id !== s.id);
    hidden = hidden.filter((x) => x.id !== s.id);
    if (to === 'all') all = [...all, { ...s, bucket: 'queue' }];
    else hidden = [{ ...s, bucket: 'hidden' }, ...hidden];
    prune();
    changed();
  };
  // the stored copy follows the server, never ahead of it: a triage that fails offline keeps it there
  const forget = (id: string) => void forgetStoredSignal(id).catch(() => undefined);
  const put = (s: Signal) => {
    if (all.some((x) => x.id === s.id) || hidden.some((x) => x.id === s.id)) return;
    adjustSignalCount(counts, s, 1);
    if (s.bucket === 'hidden') hidden = [s, ...hidden];
    else all = [...all, s];
    prune();
    changed();
  };
  const triage = createTriage({ drop, move, load, forget, put });

  const routePicks = async () => {
    if (state.planning) return;
    const picks = [...all, ...hidden].filter((s) => picked.has(s.id));
    state.planning = true;
    changed();
    try {
      // a pick that knows where it is (D80) is placed by itself; the map is asked about the rest
      const own = new Map(
        picks.flatMap((s) => {
          const at = locationOf(s);
          return at ? [[s.id, at] as const] : [];
        }),
      );
      const here = await currentPosition();
      const located = own;
      const first = picks.find((s) => located.has(s.id));
      const firstAt = first && located.get(first.id);
      if (!first || !firstAt) {
        shell.toast('None of the picked businesses could be placed on the map.', { kind: 'error' });
        return;
      }
      const anchor = routeAnchor(here, { place_id: firstAt.place_id ?? `signal:${first.id}` });
      const { route, unlocated } = planSignalRoute(picks, located, anchor, new Date().toISOString());
      picked.clear();
      commitPlannedRoute(route, unlocated.length, '/signals');
    } catch (err) {
      shell.toast(planErrorMessage(err), { kind: 'error' });
    } finally {
      state.planning = false;
      changed();
    }
  };

  const visible = () => all.filter((s) => matchesFilters(filters, s));
  return {
    ...triage,
    get all() {
      return all;
    },
    get hidden() {
      return hidden;
    },
    get summary() {
      return signalSummary({ signals: all, counts });
    },
    get list() {
      return list;
    },
    kinds,
    filters,
    picked,
    state,
    visible,
    sections: () => {
      locate();
      return sectionsOf(
        { signals: visible(), hidden: hidden.filter((s) => matchesFilters(filters, s)) },
        state.anchor,
      );
    },
    locate,
    load,
    routePicks,
    dispose: () => {
      disposed = true;
    },
  };
};
